import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from '@/components/ui/icons';
import { clsx } from 'clsx';

// Instrument-system button (Claude Design bundle §1.4): zero radius, 600 weight,
// brand colour via tokens, exact hover/disabled/focus treatments. Five variants.
export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 font-semibold whitespace-nowrap transition-colors ' +
    'focus-visible:outline-none focus-visible:shadow-[0_0_0_2px_#fff,0_0_0_4px_var(--color-focus)] ' +
    'disabled:bg-[#F1F4F8] disabled:text-muted-soft disabled:cursor-not-allowed disabled:shadow-none disabled:border-line',
  {
    variants: {
      variant: {
        primary: 'bg-button text-on-button hover:bg-[color-mix(in_srgb,var(--color-button),#000_14%)]',
        secondary: 'bg-highlight text-primary-ink hover:bg-[color-mix(in_srgb,var(--color-highlight),#000_7%)]',
        outline: 'bg-white text-ink border border-line-strong hover:bg-head',
        ghost: 'text-primary-ink hover:bg-highlight',
        danger: 'bg-destructive text-white hover:bg-destructive-hover',
        accent: 'bg-accent text-on-accent hover:bg-[color-mix(in_srgb,var(--color-accent),#000_12%)]',
      },
      size: {
        sm: 'h-8 px-3 text-[13px]',
        md: 'h-10 px-[18px] text-sm',
        lg: 'h-11 px-5 text-[15px]',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  isLoading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, isLoading, children, disabled, type, ...props }, ref) => (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={clsx(buttonVariants({ variant, size }), className)}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
      {children}
    </button>
  ),
);
Button.displayName = 'Button';
